/** `readCreateDoubtDoubtId` throws this when DC returns `errorCode` 900 (quota exhausted). */
export const CREATE_DOUBT_QUOTA_EXHAUSTED_MESSAGE = "doubt_dc_900";

/** Optional AI message id (numeric or UUID string) from create-doubt / used by Buddy APIs. */
export function readDoubtBuddyMessageId(
  res: unknown,
): string | number | undefined {
  if (res == null || typeof res !== "object") return undefined;
  const r = res as Record<string, unknown>;
  const nested =
    r.data != null && typeof r.data === "object"
      ? (r.data as Record<string, unknown>)
      : undefined;
  const pick = (
    o: Record<string, unknown> | undefined,
  ): string | number | undefined => {
    if (!o) return undefined;
    const raw = o.doubtBuddyMessageId ?? o.messageId;
    if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return raw;
    if (typeof raw === "string") {
      const s = raw.trim();
      if (!s.length) return undefined;
      const n = Number(s);
      if (Number.isFinite(n) && n > 0 && String(n) === s) return n;
      return s;
    }
    return undefined;
  };
  return pick(nested) ?? pick(r);
}

export function readCreateDoubtDoubtId(res: unknown): number {
  const r = res as Record<string, unknown> | null | undefined;
  const nested =
    r?.data != null && typeof r.data === "object"
      ? (r.data as Record<string, unknown>)
      : undefined;
  const pick = (o: Record<string, unknown> | undefined): number | undefined => {
    if (!o) return undefined;
    const raw =
      o.doubtId ?? o.doubt_id ?? o.questionId ?? o.question_id ?? o.id;
    if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return raw;
    if (typeof raw === "string") {
      const n = Number(raw.trim());
      if (Number.isFinite(n) && n > 0) return n;
    }
    return undefined;
  };
  if (r?.success !== false) {
    const doubtId = pick(nested) ?? pick(r ?? undefined);
    if (doubtId != null) return doubtId;
  }
  const code =
    typeof nested?.errorCode === "number"
      ? nested.errorCode
      : typeof r?.errorCode === "number"
        ? r.errorCode
        : undefined;
  throw new Error(
    typeof code === "number" ? `doubt_dc_${code}` : "doubt_missing_id",
  );
}

export function programIdForCreateDoubtBody(
  programId: string | null | undefined,
): string | undefined {
  if (programId == null) return undefined;
  const s = String(programId).trim();
  return s.length > 0 ? s : undefined;
}

export function isCreateDoubtQuotaExhaustedError(e: unknown): boolean {
  return (
    e instanceof Error && e.message === CREATE_DOUBT_QUOTA_EXHAUSTED_MESSAGE
  );
}

function hasInappropriateFlag(value: unknown): boolean {
  if (value == null || typeof value !== "object") return false;
  const r = value as Record<string, unknown>;
  if (r.inAppropriateImage === true || r.inappropriateImage === true) {
    return true;
  }
  const nested =
    r.data != null && typeof r.data === "object"
      ? (r.data as Record<string, unknown>)
      : undefined;
  if (
    nested?.inAppropriateImage === true ||
    nested?.inappropriateImage === true
  ) {
    return true;
  }
  return false;
}

export function readImageOcrInappropriateFlag(ocrResOrError: unknown): boolean {
  const visited = new Set<unknown>();
  const queue: unknown[] = [ocrResOrError];
  while (queue.length > 0) {
    const value = queue.shift();
    if (value == null || typeof value !== "object" || visited.has(value)) {
      continue;
    }
    visited.add(value);
    if (hasInappropriateFlag(value)) return true;
    const v = value as Record<string, unknown>;
    queue.push(v.response, v.data, v.originalError, v.details, v.error);
  }
  return false;
}

const OCR_UNREADABLE_PATTERNS: RegExp[] = [
  /\b(question(?:\s+text)?|image|content|text|options?)\b[^.]{0,40}\b(is|are|appears?|seems?|looks?)\b[^.]{0,80}\b(unreadable|illegible|undecipherable|indecipherable|impossible to read|not readable|cannot be read|out of focus)\b/i,
  /\b(unreadable|illegible|undecipherable)\b[^.]{0,60}\b(due to|because of|owing to)\b[^.]{0,80}\b(blur|blurry|blurriness|blurred|haziness|distortion|noise|low resolution|poor (?:quality|lighting|focus))\b/i,
  /\b(unable|cannot|can[' ]?t|impossible|difficult)\b[^.]{0,40}\bto\b[^.]{0,40}\b(read|identify|decipher|determine|make out|discern|extract)\b/i,
  /\bno\b[^.]{0,30}\b(discernible|readable|legible|recognis(?:e|a)ble|identifiable|clear)\b[^.]{0,40}\b(text|content|question|details|elements|information|features|markings)\b/i,
  /\b(extreme|severe|excessive|significant|complete|heavy|extensive)\b[^.]{0,30}\b(blur|blurriness|blurring|haziness|distortion)\b/i,
  /\bimage\b[^.]{0,40}\b(is|appears|looks)\b[^.]{0,40}\b(entirely|completely|totally|severely|heavily)?\s*(blurred|blurry|distorted|out of focus|too dark|too bright)\b/i,
  /\bblurr(?:ed|y|iness|ing)\b[^.]{0,40}\b(unreadable|illegible|cannot|impossible|prevents?)\b/i,
];

function matchesOcrUnreadableText(text: string | undefined): boolean {
  if (!text) return false;
  return OCR_UNREADABLE_PATTERNS.some((p) => p.test(text));
}

export function readImageOcrUnreadableFlag(ocrRes: unknown): boolean {
  if (ocrRes == null || typeof ocrRes !== "object") return false;
  const r = ocrRes as Record<string, unknown>;
  const data =
    r.data != null && typeof r.data === "object"
      ? (r.data as Record<string, unknown>)
      : r;
  const question =
    typeof data.question === "string" ? data.question.trim() : "";
  const imageDesc =
    typeof data.image_description === "string"
      ? data.image_description.trim()
      : typeof data.imageDescription === "string"
        ? data.imageDescription.trim()
        : "";
  if (matchesOcrUnreadableText(question)) return true;
  if (matchesOcrUnreadableText(imageDesc)) return true;
  // Duplicate-options heuristic: real MCQs never have all options identical.
  // If two or more match an "unreadable" pattern the OCR clearly failed.
  const options = Array.isArray(data.options) ? data.options : null;
  if (options && options.length >= 2) {
    const stringOptions = options.filter(
      (o): o is string => typeof o === "string",
    );
    if (stringOptions.length >= 2) {
      const allSame = stringOptions.every((o) => o === stringOptions[0]);
      if (allSame && matchesOcrUnreadableText(stringOptions[0])) return true;
      const matching = stringOptions.filter(matchesOcrUnreadableText);
      if (matching.length >= 2) return true;
    }
  }
  return false;
}

type ImageOcrV2IdPath = { imageId?: number; imagePath?: string };

function pickImageIdPathFromRecord(
  obj: Record<string, unknown> | null | undefined,
): ImageOcrV2IdPath {
  if (!obj) return {};
  const rawId = obj.imageId ?? obj.image_id;
  let imageId: number | undefined;
  if (typeof rawId === "number" && Number.isFinite(rawId) && rawId > 0) {
    imageId = rawId;
  } else if (typeof rawId === "string" && rawId.length > 0) {
    const n = Number(rawId);
    if (Number.isFinite(n) && n > 0) imageId = n;
  }
  const rawPath =
    obj.imagePath ?? obj.image_path ?? obj.imageUrl ?? obj.image_url;
  const imagePath =
    typeof rawPath === "string" && rawPath.trim().length > 0
      ? rawPath.trim()
      : undefined;
  return { imageId, imagePath };
}

/**
 * v2 `ImageOCR` stores the file and returns `imageId` + `imagePath` (S3 URL). `createDoubt`
 * must include them or list/detail (e.g. getDoubtV2) will not treat the doubt as an image doubt.
 */
export function readImageOcrV2ImageFields(ocrRes: unknown): ImageOcrV2IdPath {
  if (ocrRes == null || typeof ocrRes !== "object") {
    return {};
  }
  const r = ocrRes as { data?: unknown } & Record<string, unknown>;
  const fromData =
    r.data != null && typeof r.data === "object"
      ? pickImageIdPathFromRecord(r.data as Record<string, unknown>)
      : {};
  const fromRoot = pickImageIdPathFromRecord(r);
  return {
    imageId: fromData.imageId ?? fromRoot.imageId,
    imagePath: fromData.imagePath ?? fromRoot.imagePath,
  };
}
